import { CHUNK_TYPE } from '~/constants/conversationModels';
import veevaLogo from '~/assets/svg/veeva-logo.svg';

import { isVeevaDocument } from '~/utils/document-utils';

import {
  extractCitationEntries,
  extractReferencesFromCitations,
  getRenderedCitationEntries,
  buildRefNumCitationDict,
} from '~/utils/citation-placeholders';
import { buildSecondOrderReferencesBySnippet, buildSecondOrderReferences } from '~/utils/citation-second-order';

/**
 * Enriches a message's legacy documentList block with `citedReferences` (one per distinct cited
 * document) and `secondOrderReferences` (one per unique second-order source, labeled/deduped —
 * see buildSecondOrderReferences). Runs once when a conversation is loaded (see
 * useConversations.js/conversationStore.js) — the result is cached on message.data[0], not
 * recomputed reactively, so a conversation must be reloaded to pick up logic changes here.
 * @param {string} messageText - Raw message text (for extracting citation placeholders).
 * @param {object[]} blockData - message.data; must contain a `documentList` block.
 * @param {boolean} useNewAgent - Whether snippet text is already decoded (new-agent messages) vs
 *   base64-encoded (legacy) — see normalizeSecondOrderReference's decodedSnippet.
 * @returns {object[]} `blockData` unchanged (same reference) if it's empty or has no `documentList`
 *   block; otherwise a new array with that block's citedReferences/secondOrderReferences replaced.
 */
export const processCitedReferences = (messageText, blockData, useNewAgent = false) => {
  if (!blockData || blockData.length === 0) return blockData;

  const documentListBlock = blockData.find((block) => block.type === 'documentList');
  if (!documentListBlock) return blockData;

  const citedRefs = extractReferencesFromCitations(messageText || '');
  const references = Array.isArray(documentListBlock.references) ? documentListBlock.references : [];
  const citedReferences = citedRefs
    .map((citedRef) => references.find((ref) => ref.externalId === citedRef.documentId))
    .filter(Boolean);

  const citationEntries = extractCitationEntries(messageText || '');
  const secondOrderReferencesBySnippet = buildSecondOrderReferencesBySnippet({
    references,
    renderedCitationEntries: extractCitationEntries(messageText || ''),
    useNewAgent,
  });
  const citationLabelDict = buildRefNumCitationDict(citationEntries, references);
  const secondOrderReferences = buildSecondOrderReferences(
    { message: messageText || '' },
    secondOrderReferencesBySnippet,
    citationLabelDict,
  );

  return [{ ...documentListBlock, citedReferences, secondOrderReferences }];
};

// A new-agent message never has the legacy documentList block (message.data[0]) — it
// carries references in message.chunks instead. Both shapes coexist long-term (Assist on
// the new agent, Authoring on the old agent for the foreseeable future), so this is
// decided per-message rather than from a global "which agent is active" flag.
export const isNewAgentMessage = (message = {}) => {
  if (message.data?.[0]) return false;
  return (message.chunks || []).some((chunk) => chunk.type === CHUNK_TYPE.REFERENCES);
};

// Resolves { references, secondOrderReferences } for a message regardless of which flow produced it:
// the legacy documentList block (message.data[0]) or the new-agent SSE REFERENCES chunk
// (message.chunks). InfoTooltip only has the raw message to work from, unlike ReferenceCardsList
// which already receives the right slice as a prop.
export const getMessageReferencesData = (message = {}) => {
  const legacyBlock = message.data?.[0];
  if (legacyBlock) {
    return {
      references: Array.isArray(legacyBlock.references) ? legacyBlock.references : [],
      secondOrderReferences: Array.isArray(legacyBlock.secondOrderReferences) ? legacyBlock.secondOrderReferences : [],
    };
  }

  const referencesChunk = (message.chunks || []).find((chunk) => chunk.type === CHUNK_TYPE.REFERENCES);
  const references = Array.isArray(referencesChunk?.data?.references) ? referencesChunk.data.references : [];
  const renderedCitationEntries = getRenderedCitationEntries(message);
  const secondOrderReferencesBySnippet = buildSecondOrderReferencesBySnippet({
    references,
    renderedCitationEntries,
    // Reaching this branch already proves it's a new-agent message (see isNewAgentMessage above).
    useNewAgent: true,
  });
  const citationLabelDict = buildRefNumCitationDict(renderedCitationEntries, references);
  const secondOrderReferences = buildSecondOrderReferences(message, secondOrderReferencesBySnippet, citationLabelDict);

  return { references, secondOrderReferences };
};

/**
 * Source badge data (name/logo/type) for a document id — currently only distinguishes Veeva
 * (internal) documents from everything else (external, and defaulted to unsubstantiated).
 * @param {string} id - Document externalId; falsy (or non-Veeva) resolves to the 'external'/'Unknown' default.
 * @returns {{name: string, logo: string|null, type: 'internal'|'external', unsubstantiated: boolean}}
 */
export const getCitationSourceData = (id) => {
  if (id) {
    const isVeevaId = isVeevaDocument(id);

    if (isVeevaId) {
      return {
        name: 'Veeva',
        logo: veevaLogo,
        type: 'internal',
        unsubstantiated: false,
      };
    }
  }

  return {
    name: 'Unknown',
    logo: null,
    type: 'external',
    unsubstantiated: true,
  };
};
